import { useEffect, useSyncExternalStore, type SetStateAction } from 'react';
import { readQualificationDraft, saveQualificationDraft, qualificationDraftSnapshot, subscribeQualificationDraft, type QualificationDraft, type IntakeMeasurements } from '../store/qualificationDraft';
import type { CheckoutQualificationAnswers } from '../lib/combinedQualification';

const EMPTY: QualificationDraft = { answers: {}, measurements: { biologicalSex: '', heightFt: '', heightIn: '', weightLbs: '' } };

export function useQualificationDraft(scope: string) {
  const state = useSyncExternalStore(subscribeQualificationDraft, qualificationDraftSnapshot, qualificationDraftSnapshot);
  // A different patient/clinic must never render the previous patient's draft.
  const draft = state?.scope === scope ? state.draft : EMPTY;
  useEffect(() => { readQualificationDraft(scope); }, [scope]);
  function update<K extends keyof QualificationDraft>(field: K, value: SetStateAction<QualificationDraft[K]>) {
    const current = readQualificationDraft(scope);
    const next = { ...current, [field]: typeof value === 'function' ? (value as (old: QualificationDraft[K]) => QualificationDraft[K])(current[field]) : value };
    saveQualificationDraft(scope, next);
  }
  return {
    programs: draft.programs || {},
    setPrograms: (value: SetStateAction<NonNullable<QualificationDraft["programs"]>>) => {
      const current = readQualificationDraft(scope);
      saveQualificationDraft(scope, { ...current, programs: typeof value === "function" ? value(current.programs || {}) : value });
    },
    answers: draft.answers, measurements: draft.measurements,
    setAnswers: (value: SetStateAction<CheckoutQualificationAnswers>) => update('answers', value),
    setMeasurements: (value: SetStateAction<IntakeMeasurements>) => update('measurements', value),
    transferTo: (nextScope: string) => saveQualificationDraft(nextScope, draft),
  };
}
